import { IDataDriver } from "../types";
import { IAnyObj } from "@dhx/ts-common/types";
import { jsonToXML } from "./../serializers/xml";

const ARRAY_NAME = "items";
const ITEM_NAME = "item";


// convert xml tag to js object, all subtags and attributes are mapped to the properties of result object
function tagToObject(tag: Element, initialObj?: { [key: string]: any }) {
	initialObj = initialObj || {};


	// map attributes
	const a = tag.attributes;
	if (a && a.length) {
		for (let i = 0; i < a.length; i++) {
			initialObj[a[i].name] = a[i].value;
		}
	}

	// map subtags
	const b = tag.childNodes;
	for (let i = 0; i < b.length; i++) {
		if (b[i].nodeType === 1) {
			const name = (b[i] as Element).tagName;
			if (initialObj[name]) {
				if (typeof initialObj[name].push !== "function") {
					initialObj[name] = [initialObj[name]];
				}
				initialObj[name].push(tagToObject(b[i] as Element, {}));

			} else {
				initialObj[name] = tagToObject(b[i] as Element, {});	// sub-object for complex subtags
			}
		}
	}

	return initialObj;
}

export class XMLDriver implements IDataDriver {
	toJsonArray(data: any) {
		return this.getRows(data);
	}
	toJsonObject(data: string) {
		let doc: Element;
		if (typeof data === "string") {
			doc = this._fromString(data) as unknown as Element;
		}

		return tagToObject(doc);
	}
	serialize(data: IAnyObj[]) {
		return jsonToXML(data);
	}
	getFields(row: any): { [key: string]: any } {
		return row;
	}
	getRows(data: Document | string): any[] {
		if (typeof data === "string") {
			data = this._fromString(data);
		}
		if (data) {
			const childNodes = data.childNodes && data.childNodes[0] && data.childNodes[0].childNodes;
			if (!childNodes || !childNodes.length) {
				return null;
			}
			return this._getRows(childNodes);
		}
		return [];
	}

	private _getRows(nodes: NodeListOf<ChildNode>): any[] {
		const result = [];
		for (let i = 0; i < nodes.length; i++) {
			if ((nodes[i] as HTMLElement).tagName === ITEM_NAME) {
				result.push(this._nodeToJS(nodes[i] as HTMLElement));
			}
		}
		return result;
	}
	private _fromString(data: string): Document {
		try {
			return (new DOMParser()).parseFromString(data, "text/xml");
		} catch {
			return null;
		}
	}

	private _nodeToJS(node: HTMLElement) {
		const result: IAnyObj = {};

		if (this._haveAttrs(node)) {
			const attrs = node.attributes;
			for (let i = 0; i < attrs.length; i++) {
				const { name, value } = attrs[i];
				result[name] = this._toType(value);
			}
		}
		if (node.nodeType === 3) {
			result.value = result.value || this._toType(node.textContent);
			return result;
		}

		const childNodes = node.childNodes;
		if (childNodes) {
			for (let i = 0; i < childNodes.length; i++) {
				const subNode = childNodes[i] as HTMLElement;
				const tag = subNode.tagName;
				if (!tag) {
					continue;
				}
				if (tag === ARRAY_NAME && subNode.childNodes) {
					result[tag] = this._getRows(subNode.childNodes);
				} else {
					if (this._haveAttrs(subNode)) {
						result[tag] = this._nodeToJS(subNode);
					} else {
						result[tag] = this._toType(subNode.textContent);
					}
				}
			}
		}

		return result;
	}
	private _toType(val: any) {
		if (val === "false" || val === "true") {
			return val === "true";
		}
		if (!isNaN(val)) {
			return Number(val);
		}

		return val;
	}
	private _haveAttrs(node: HTMLElement) {
		return node.attributes && node.attributes.length;
	}
}